import React, { useState, useEffect } from 'react';
import { Search, Filter, ChevronDown, Eye, X, Building2, Briefcase, Clock, CheckCircle, XCircle, Phone, Mail, MapPin, Calendar, User } from 'lucide-react';
import { dbProxy } from '../lib/db-proxy';

type Tab = 'franchise' | 'jobs';
type StatusFilter = 'tutti' | 'nuovo' | 'in_revisione' | 'contattato' | 'approvato' | 'rifiutato';

interface FranchiseApplication {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  city: string;
  region: string | null;
  investment_budget: string | null;
  experience: string | null;
  motivation: string | null;
  has_location: boolean;
  location_details: string | null;
  notes: string | null;
  status: string;
  created_at: string;
}

interface JobApplication {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  city: string;
  position: string;
  experience: string | null;
  motivation: string | null;
  availability: string | null;
  notes: string | null;
  status: string;
  created_at: string;
}

const statusLabels: Record<string, string> = {
  nuovo: 'Nuovo',
  in_revisione: 'In Revisione',
  contattato: 'Contattato',
  approvato: 'Approvato',
  rifiutato: 'Rifiutato',
};

const statusColors: Record<string, string> = {
  nuovo: 'bg-blue-100 text-blue-800',
  in_revisione: 'bg-amber-100 text-amber-800',
  contattato: 'bg-cyan-100 text-cyan-800',
  approvato: 'bg-green-100 text-green-800',
  rifiutato: 'bg-red-100 text-red-800',
};

const Candidature = () => {
  const [activeTab, setActiveTab] = useState<Tab>('franchise');
  const [franchiseApps, setFranchiseApps] = useState<FranchiseApplication[]>([]);
  const [jobApps, setJobApps] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('tutti');
  const [selectedFranchise, setSelectedFranchise] = useState<FranchiseApplication | null>(null);
  const [selectedJob, setSelectedJob] = useState<JobApplication | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [franchiseRes, jobRes] = await Promise.all([
        dbProxy.from('franchise_applications').select('*').order('created_at', { ascending: false }),
        dbProxy.from('job_applications').select('*').order('created_at', { ascending: false }),
      ]);
      if (franchiseRes.data) setFranchiseApps(franchiseRes.data);
      if (jobRes.data) setJobApps(jobRes.data);
    } catch (err) {
      console.error('Error loading applications:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateFranchiseStatus = async (id: string, newStatus: string) => {
    setUpdatingStatus(true);
    try {
      const { error } = await dbProxy.from('franchise_applications').update({ status: newStatus }).eq('id', id);
      if (!error) {
        setFranchiseApps(prev => prev.map(a => a.id === id ? { ...a, status: newStatus } : a));
        if (selectedFranchise?.id === id) setSelectedFranchise(prev => prev ? { ...prev, status: newStatus } : null);
      }
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const updateJobStatus = async (id: string, newStatus: string) => {
    setUpdatingStatus(true);
    try {
      const { error } = await dbProxy.from('job_applications').update({ status: newStatus }).eq('id', id);
      if (!error) {
        setJobApps(prev => prev.map(a => a.id === id ? { ...a, status: newStatus } : a));
        if (selectedJob?.id === id) setSelectedJob(prev => prev ? { ...prev, status: newStatus } : null);
      }
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const filterItems = <T extends { full_name: string; email: string; city: string; status: string }>(items: T[]): T[] => {
    return items.filter(item => {
      const matchesSearch = !searchQuery ||
        item.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.city.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'tutti' || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  };

  const filteredFranchise = filterItems(franchiseApps);
  const filteredJobs = filterItems(jobApps);

  const formatDate = (d: string) => {
    const date = new Date(d);
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (d: string) => {
    const date = new Date(d);
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const franchiseCount = franchiseApps.filter(a => a.status === 'nuovo').length;
  const jobCount = jobApps.filter(a => a.status === 'nuovo').length;

  return (
    <div className="p-4 lg:p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Candidature</h1>
        <p className="text-gray-500 mt-1">Gestisci le richieste di affiliazione e le candidature di lavoro</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('franchise')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${
            activeTab === 'franchise'
              ? 'bg-[#D91C5C] text-white shadow-sm'
              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
          }`}
        >
          <Building2 className="h-4 w-4" />
          Affiliazioni
          {franchiseCount > 0 && (
            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
              activeTab === 'franchise' ? 'bg-white/20 text-white' : 'bg-[#D91C5C] text-white'
            }`}>
              {franchiseCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('jobs')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${
            activeTab === 'jobs'
              ? 'bg-[#D91C5C] text-white shadow-sm'
              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
          }`}
        >
          <Briefcase className="h-4 w-4" />
          Lavoro
          {jobCount > 0 && (
            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
              activeTab === 'jobs' ? 'bg-white/20 text-white' : 'bg-[#D91C5C] text-white'
            }`}>
              {jobCount}
            </span>
          )}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca per nome, email o citta'..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#D91C5C]/20 focus:border-[#D91C5C]"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as StatusFilter)}
            className="pl-10 pr-8 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#D91C5C]/20 focus:border-[#D91C5C] appearance-none bg-white"
          >
            <option value="tutti">Tutti gli stati</option>
            <option value="nuovo">Nuovo</option>
            <option value="in_revisione">In Revisione</option>
            <option value="contattato">Contattato</option>
            <option value="approvato">Approvato</option>
            <option value="rifiutato">Rifiutato</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 border-3 border-[#D91C5C]/20 border-t-[#D91C5C] rounded-full animate-spin" />
        </div>
      ) : activeTab === 'franchise' ? (
        /* Franchise Applications Table */
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {filteredFranchise.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">Nessuna richiesta di affiliazione</p>
              <p className="text-sm mt-1">Le nuove richieste appariranno qui</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Nome</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Citta'</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Budget</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Locale</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Data</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Stato</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredFranchise.map(app => (
                    <tr key={app.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-4">
                        <div className="font-medium text-gray-900 text-sm">{app.full_name}</div>
                        <div className="text-xs text-gray-500">{app.email}</div>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600">{app.city}{app.region ? `, ${app.region}` : ''}</td>
                      <td className="px-5 py-4 text-sm text-gray-600">{app.investment_budget || '-'}</td>
                      <td className="px-5 py-4">
                        {app.has_location ? (
                          <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium"><CheckCircle className="h-3.5 w-3.5" /> Si'</span>
                        ) : (
                          <span className="text-xs text-gray-400">No</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-500">{formatDate(app.created_at)}</td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[app.status] || 'bg-gray-100 text-gray-600'}`}>
                          {statusLabels[app.status] || app.status}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <button
                          onClick={() => setSelectedFranchise(app)}
                          className="text-[#D91C5C] hover:text-[#C01850] transition-colors"
                          title="Vedi dettagli"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        /* Job Applications Table */
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {filteredJobs.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <Briefcase className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">Nessuna candidatura di lavoro</p>
              <p className="text-sm mt-1">Le nuove candidature appariranno qui</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Nome</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Posizione</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Citta'</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Disponibilita'</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Data</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Stato</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredJobs.map(app => (
                    <tr key={app.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-4">
                        <div className="font-medium text-gray-900 text-sm">{app.full_name}</div>
                        <div className="text-xs text-gray-500">{app.email}</div>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600">{app.position}</td>
                      <td className="px-5 py-4 text-sm text-gray-600">{app.city}</td>
                      <td className="px-5 py-4 text-sm text-gray-500">{app.availability || '-'}</td>
                      <td className="px-5 py-4 text-sm text-gray-500">{formatDate(app.created_at)}</td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[app.status] || 'bg-gray-100 text-gray-600'}`}>
                          {statusLabels[app.status] || app.status}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <button
                          onClick={() => setSelectedJob(app)}
                          className="text-[#D91C5C] hover:text-[#C01850] transition-colors"
                          title="Vedi dettagli"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Franchise Detail Modal */}
      {selectedFranchise && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedFranchise(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Richiesta Affiliazione</h2>
                <p className="text-sm text-gray-500">Ricevuta il {formatDateTime(selectedFranchise.created_at)}</p>
              </div>
              <button onClick={() => setSelectedFranchise(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Contact info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Dati Personali</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center"><User className="h-4 w-4 text-gray-500" /></div>
                    <div>
                      <div className="text-xs text-gray-400">Nome</div>
                      <div className="text-sm font-medium text-gray-900">{selectedFranchise.full_name}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center"><Mail className="h-4 w-4 text-gray-500" /></div>
                    <div>
                      <div className="text-xs text-gray-400">Email</div>
                      <a href={`mailto:${selectedFranchise.email}`} className="text-sm font-medium text-[#D91C5C] hover:underline">{selectedFranchise.email}</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center"><Phone className="h-4 w-4 text-gray-500" /></div>
                    <div>
                      <div className="text-xs text-gray-400">Telefono</div>
                      <a href={`tel:${selectedFranchise.phone}`} className="text-sm font-medium text-[#D91C5C] hover:underline">{selectedFranchise.phone}</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center"><MapPin className="h-4 w-4 text-gray-500" /></div>
                    <div>
                      <div className="text-xs text-gray-400">Zona</div>
                      <div className="text-sm font-medium text-gray-900">{selectedFranchise.city}{selectedFranchise.region ? `, ${selectedFranchise.region}` : ''}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Business info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Dettagli Business</h3>
                <div className="space-y-3">
                  {selectedFranchise.investment_budget && (
                    <div className="bg-gray-50 rounded-xl px-4 py-3">
                      <div className="text-xs text-gray-400 mb-1">Budget di Investimento</div>
                      <div className="text-sm font-medium text-gray-900">{selectedFranchise.investment_budget}</div>
                    </div>
                  )}
                  <div className="bg-gray-50 rounded-xl px-4 py-3">
                    <div className="text-xs text-gray-400 mb-1">Ha un locale disponibile?</div>
                    <div className="text-sm font-medium text-gray-900">{selectedFranchise.has_location ? 'Si\'' : 'No'}</div>
                    {selectedFranchise.location_details && <div className="text-sm text-gray-600 mt-1">{selectedFranchise.location_details}</div>}
                  </div>
                  {selectedFranchise.experience && (
                    <div className="bg-gray-50 rounded-xl px-4 py-3">
                      <div className="text-xs text-gray-400 mb-1">Esperienza</div>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap">{selectedFranchise.experience}</div>
                    </div>
                  )}
                  {selectedFranchise.motivation && (
                    <div className="bg-gray-50 rounded-xl px-4 py-3">
                      <div className="text-xs text-gray-400 mb-1">Motivazione</div>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap">{selectedFranchise.motivation}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Status update */}
              <div>
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Stato</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(statusLabels).map(([key, label]) => (
                    <button
                      key={key}
                      disabled={updatingStatus}
                      onClick={() => updateFranchiseStatus(selectedFranchise.id, key)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        selectedFranchise.status === key
                          ? `${statusColors[key]} ring-2 ring-offset-1 ring-current`
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Job Detail Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedJob(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Candidatura Lavoro</h2>
                <p className="text-sm text-gray-500">Ricevuta il {formatDateTime(selectedJob.created_at)}</p>
              </div>
              <button onClick={() => setSelectedJob(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Dati Personali</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center"><User className="h-4 w-4 text-gray-500" /></div>
                    <div>
                      <div className="text-xs text-gray-400">Nome</div>
                      <div className="text-sm font-medium text-gray-900">{selectedJob.full_name}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center"><Mail className="h-4 w-4 text-gray-500" /></div>
                    <div>
                      <div className="text-xs text-gray-400">Email</div>
                      <a href={`mailto:${selectedJob.email}`} className="text-sm font-medium text-[#D91C5C] hover:underline">{selectedJob.email}</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center"><Phone className="h-4 w-4 text-gray-500" /></div>
                    <div>
                      <div className="text-xs text-gray-400">Telefono</div>
                      <a href={`tel:${selectedJob.phone}`} className="text-sm font-medium text-[#D91C5C] hover:underline">{selectedJob.phone}</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center"><MapPin className="h-4 w-4 text-gray-500" /></div>
                    <div>
                      <div className="text-xs text-gray-400">Citta'</div>
                      <div className="text-sm font-medium text-gray-900">{selectedJob.city}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Dettagli Candidatura</h3>
                <div className="space-y-3">
                  <div className="bg-gray-50 rounded-xl px-4 py-3">
                    <div className="text-xs text-gray-400 mb-1">Posizione</div>
                    <div className="text-sm font-medium text-gray-900">{selectedJob.position}</div>
                  </div>
                  {selectedJob.availability && (
                    <div className="bg-gray-50 rounded-xl px-4 py-3">
                      <div className="text-xs text-gray-400 mb-1">Disponibilita'</div>
                      <div className="text-sm text-gray-700">{selectedJob.availability}</div>
                    </div>
                  )}
                  {selectedJob.experience && (
                    <div className="bg-gray-50 rounded-xl px-4 py-3">
                      <div className="text-xs text-gray-400 mb-1">Esperienza</div>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap">{selectedJob.experience}</div>
                    </div>
                  )}
                  {selectedJob.motivation && (
                    <div className="bg-gray-50 rounded-xl px-4 py-3">
                      <div className="text-xs text-gray-400 mb-1">Motivazione</div>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap">{selectedJob.motivation}</div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Stato</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(statusLabels).map(([key, label]) => (
                    <button
                      key={key}
                      disabled={updatingStatus}
                      onClick={() => updateJobStatus(selectedJob.id, key)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        selectedJob.status === key
                          ? `${statusColors[key]} ring-2 ring-offset-1 ring-current`
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Candidature;
